import { Check, Loader2, NotebookPen, Plus, X } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { useAddMinute, useDeleteMinute, useUpdateMinute } from '../../api'
import type { Meeting, MinuteItem, Priority } from '../../types'
import { toastError } from '../../ui'
import { MinutesExportMenu, PriorityPicker } from './MinuteBits'

export function MinuteRow({ meetingId, item, canWrite, large }: { meetingId: string; item: MinuteItem; canWrite: boolean; large?: boolean }) {
  const { t } = useTranslation()
  const update = useUpdateMinute()
  const remove = useDeleteMinute()
  const [draft, setDraft] = useState<string | null>(null)

  const save = () => {
    if (draft === null) return
    if (draft.trim() === item.text || draft.trim().length < 2) return setDraft(null)
    update.mutate({ meetingId, itemId: item.id, patch: { text: draft } }, { onSuccess: () => setDraft(null), onError: toastError })
  }

  return (
    <li className={cn('group flex items-start gap-3 rounded-lg px-2 transition-colors hover:bg-muted/50', large ? 'py-2.5' : 'py-1.5')}>
      <button
        type="button"
        role="checkbox"
        aria-checked={item.done}
        aria-label={item.text}
        disabled={!canWrite}
        onClick={() => update.mutate({ meetingId, itemId: item.id, patch: { done: !item.done } }, { onError: toastError })}
        className={cn(
          'mt-0.5 flex shrink-0 items-center', large ? 'size-6' : 'size-5', 'justify-center rounded-full border-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default',
          item.done ? 'border-success bg-success text-white' : 'border-muted-foreground/40 hover:border-primary',
        )}
      >
        {item.done ? <Check className="size-3" strokeWidth={3} aria-hidden /> : null}
      </button>
      {draft !== null ? (
        <Input
          autoFocus
          size={1}
          className={cn('flex-1', large ? 'h-9 text-base' : 'h-7 text-sm')}
          value={draft}
          maxLength={300}
          aria-label={t('crm.minutes.editItem')}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => {
            if (e.key === 'Enter') save()
            if (e.key === 'Escape') setDraft(null)
          }}
        />
      ) : (
        <p
          className={cn('min-w-0 flex-1 break-words', large ? 'text-base' : 'text-sm', item.done && 'text-muted-foreground line-through', canWrite && 'cursor-text')}
          onDoubleClick={canWrite ? () => setDraft(item.text) : undefined}
          title={canWrite ? t('crm.minutes.editHint') : undefined}
        >
          {item.text}
        </p>
      )}
      {draft === null ? (
        <PriorityPicker
          compact={!large}
          value={item.priority}
          onChange={canWrite ? (priority) => priority !== item.priority && update.mutate({ meetingId, itemId: item.id, patch: { priority } }, { onError: toastError }) : undefined}
        />
      ) : null}
      {canWrite && draft === null ? (
        <Button
          size="icon"
          variant="ghost"
          className="size-6 shrink-0 text-muted-foreground opacity-100 hover:text-destructive sm:opacity-0 sm:group-hover:opacity-100 sm:focus-visible:opacity-100"
          aria-label={t('crm.minutes.removeItem')}
          disabled={remove.isPending}
          onClick={() => remove.mutate({ meetingId, itemId: item.id }, { onError: toastError })}
        >
          <X />
        </Button>
      ) : null}
    </li>
  )
}

/** Toplantı notları: yapılacaklar listesi gibi; yaz, Enter'a bas, işaretle. Ulaşılamayan görüşmede gösterilmez. */
export function MeetingMinutes({ meeting: m, canWrite, large }: { meeting: Meeting; canWrite: boolean; large?: boolean }) {
  const { t } = useTranslation()
  const add = useAddMinute()
  const [text, setText] = useState('')
  const [priority, setPriority] = useState<Priority>('medium')
  if (m.result === 'not_reached') return null

  const done = m.minutes.filter((i) => i.done).length
  const submit = () => {
    if (text.trim().length < 2) return
    add.mutate({ meetingId: m.id, text, priority }, { onSuccess: () => setText(''), onError: toastError })
  }

  return (
    <section className="space-y-2">
      <div className="flex items-center gap-2">
        <NotebookPen className="size-4 text-primary" aria-hidden />
        <h3 className="flex-1 text-sm font-semibold">{t('crm.minutes.title')}</h3>
        {m.minutes.length ? (
          <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium tabular-nums text-muted-foreground">
            {done}/{m.minutes.length}
          </span>
        ) : null}
        <MinutesExportMenu meeting={m} />
      </div>

      {m.minutes.length ? (
        <ul className="-mx-2">
          {m.minutes.map((i) => (
            <MinuteRow key={i.id} meetingId={m.id} item={i} canWrite={canWrite} large={large} />
          ))}
        </ul>
      ) : (
        <p className="text-xs text-muted-foreground">{t('crm.minutes.empty')}</p>
      )}

      {canWrite ? (
        <form
          className="flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            submit()
          }}
        >
          <PriorityPicker value={priority} onChange={setPriority} large={large} />
          <Input autoFocus={large} className={cn(large && 'h-12 text-base')} value={text} maxLength={300} onChange={(e) => setText(e.target.value)} placeholder={t('crm.minutes.placeholder')} aria-label={t('crm.minutes.placeholder')} />
          <Button type="submit" size="icon" variant={large ? 'default' : 'outline'} className={cn('shrink-0', large && 'size-12')} disabled={text.trim().length < 2 || add.isPending} aria-label={t('crm.minutes.add')}>
            {add.isPending ? <Loader2 className="animate-spin" /> : <Plus />}
          </Button>
        </form>
      ) : null}
    </section>
  )
}
